import { FC, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  MapPin,
  Search,
  Heart,
  Plus,
  CheckCircle2,
  FileText,
  User,
  LogIn,
  Home,
  ScanSearch,
  LucideIcon,
  TriangleAlert,
  ArrowRight,
  Menu as MenuIcon,
  X,
  BarChart3,
} from 'lucide-react';

import { useAuthToken } from '@/hooks/useAuthToken';
import { NbButton } from '@/components/Neobrut';

type MenuItem = {
  label: string;
  path: string;
  icon: LucideIcon;
  fill: string;
};

const PUBLIC_ITEMS: MenuItem[] = [
  { label: 'Inicio', path: '/', icon: Home, fill: 'fill-paper' },
  { label: 'Mapa', path: '/map', icon: MapPin, fill: 'fill-found' },
  { label: 'Buscar por foto', path: '/search-by-photo', icon: ScanSearch, fill: 'fill-paper' },
  { label: 'Perdidos', path: '/lost', icon: TriangleAlert, fill: 'fill-lost' },
  { label: 'Encontrados', path: '/found', icon: Search, fill: 'fill-found' },
  { label: 'Casos resueltos', path: '/solved-cases', icon: CheckCircle2, fill: 'fill-solved' },
  { label: 'Impacto', path: '/impacto', icon: BarChart3, fill: 'fill-solved' },
];

const AUTH_ITEMS: MenuItem[] = [
  { label: 'Publicar', path: '/publish', icon: Plus, fill: 'fill-urgent' },
  { label: 'Mis mascotas', path: '/my-pets', icon: Heart, fill: 'fill-urgent' },
  { label: 'Mis publicaciones', path: '/my-publications', icon: FileText, fill: 'fill-lost' },
  { label: 'Favoritos', path: '/my-favorites', icon: Heart, fill: 'fill-found' },
];

interface MenuItemsProps {
  closeMenuFunc: (value: boolean) => void;
}

const MenuItems: FC<MenuItemsProps> = ({ closeMenuFunc }) => {
  const { token, isTokenExpired } = useAuthToken();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const authenticated = Boolean(token && !isTokenExpired(token));

  const handleNavigate = (path: string, requiresAuth: boolean = false) => {
    if (requiresAuth && !authenticated) {
      navigate('/login', { replace: true });
    } else {
      navigate(path, { replace: true });
    }
    closeMenuFunc(false);
  };

  return (
    <div
      className="animate-in fade-in slide-in-from-top-2 nb-scroll-x absolute right-0 top-[calc(100%+8px)] z-50 flex w-[min(300px,calc(100vw-24px))] flex-col gap-2 overflow-y-auto p-3 duration-200"
      style={{
        background: 'var(--nb-paper)',
        border: '3px solid var(--nb-ink)',
        boxShadow: '6px 6px 0 var(--nb-ink)',
        borderRadius: 'var(--nb-radius)',
        maxHeight: 'calc(100vh - 100px)',
      }}
    >
      <div
        className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        / navegación
      </div>

      {PUBLIC_ITEMS.map((item) => (
        <MenuRow
          key={item.path}
          item={item}
          active={isActive(item.path)}
          onClick={() => handleNavigate(item.path)}
        />
      ))}

      <div className="nb-hairline my-1" />

      {authenticated ? (
        <>
          {AUTH_ITEMS.map((item) => (
            <MenuRow
              key={item.path}
              item={item}
              active={isActive(item.path)}
              onClick={() => handleNavigate(item.path, true)}
            />
          ))}
          <div className="nb-hairline my-1" />
          <MenuRow
            item={{ label: 'Mi cuenta', path: '/my-account', icon: User, fill: 'fill-paper' }}
            active={isActive('/my-account')}
            onClick={() => handleNavigate('/my-account', true)}
          />
        </>
      ) : (
        <MenuRow
          item={{ label: 'Iniciar sesión', path: '/login', icon: LogIn, fill: 'fill-paper' }}
          active={isActive('/login')}
          onClick={() => handleNavigate('/login')}
        />
      )}

      <NbButton
        fill="ink"
        className="mt-2 w-full py-3"
        onClick={() => handleNavigate('/publish', authenticated)}
      >
        <Plus size={16} /> Publicar mascota
      </NbButton>
    </div>
  );
};

const MenuRow: FC<{ item: MenuItem; active: boolean; onClick: () => void }> = ({
  item,
  active,
  onClick,
}) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      className={`nb-sm flex items-center gap-2.5 px-3 py-2 text-left ${item.fill}`}
      style={active ? { outline: '2px solid var(--nb-ink)', outlineOffset: 2 } : undefined}
    >
      <Icon size={16} strokeWidth={2} className="shrink-0 opacity-80" />
      <span className="font-display flex-1 text-[15px] leading-none">{item.label}</span>
      <ArrowRight size={13} className="shrink-0 opacity-60" />
    </button>
  );
};

export const Menu: FC = () => {
  const [isOpen, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative shrink-0">
      <NbButton
        fill="accent"
        className="gap-2 px-3 py-2 text-[14px]"
        aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={isOpen}
        onClick={() => setOpen((v) => !v)}
      >
        {isOpen ? <X size={18} /> : <MenuIcon size={18} />}
        <b>Menú</b>
      </NbButton>

      {isOpen && <MenuItems closeMenuFunc={setOpen} />}
    </div>
  );
};
